import { useState } from 'react';

/**
 * OH-205 §4: when the request is more than 5% off plan, the first press on
 * Confirm only arms the button; the second press sends exactly the body that
 * was on screen at the first. Any change of the body — a field, a line, the
 * account, the date, the mode, a new preview base (Keep my amounts / Apply
 * after a 409) — disarms it, a return to the earlier body included. No modal.
 * A send leaves it armed: a retry of the unchanged body after a failure is
 * one press — the operator already confirmed exactly that body.
 *
 * `fingerprint` is JSON.stringify of the request body without operation_id;
 * null while there is nothing to send. `needsSecond` must follow from the same
 * data as `fingerprint` (the armed state clears only when the fingerprint
 * changes), and a window passes a null fingerprint while its draft is not ready.
 */
export function useConfirmTwice(fingerprint: string | null, needsSecond: boolean): {
  armed: boolean; // the button shows its confirming text
  press: () => boolean; // true — send now; false — only armed (or nothing to send)
} {
  // The fingerprint the first press saw; the button is armed for that body only.
  const [armedFor, setArmedFor] = useState<string | null>(null);
  // The previous fingerprint lives in state and is compared during render — React's
  // pattern for state that follows a changed input, with no effect. Any change
  // resets armedFor, so a return to the earlier body starts from zero too.
  const [prevFingerprint, setPrevFingerprint] = useState(fingerprint);
  if (fingerprint !== prevFingerprint) {
    setPrevFingerprint(fingerprint);
    setArmedFor(null);
  }

  // armedFor is the one of the render this press came from. Two clicks are two
  // discrete events and React re-renders between them, so the second sees the first.
  const press = (): boolean => {
    if (fingerprint === null) return false;
    if (!needsSecond) return true;
    if (armedFor === fingerprint) return true;
    setArmedFor(fingerprint);
    return false;
  };

  return { armed: needsSecond && armedFor !== null && armedFor === fingerprint, press };
}
